import React, { useEffect } from 'react';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useToastStore, useUIStore } from '@/stores';
import type { ToastType } from '@/stores/toast-store';
import { clsx } from 'clsx';

const AUTO_DISMISS_MS = 3000;

export const Toast: React.FC = () => {
  const toasts = useToastStore((state) => state.toasts);
  const removeToast = useToastStore((state) => state.removeToast);
  const showFilterSidebar = useUIStore((state) => state.showFilterSidebar);
  const filterSidebarCollapsed = useUIStore((state) => state.filterSidebarCollapsed);

  return (
    <div className={clsx(
      'fixed bottom-4 z-50 flex flex-col gap-2 transition-all duration-300',
      showFilterSidebar
        ? filterSidebarCollapsed ? 'right-[5.5rem]' : 'right-[19.5rem]'
        : 'right-4'
    )}>
      {toasts.map((toast) => (
        <ToastItem
          key={toast.id}
          id={toast.id}
          type={toast.type}
          message={toast.message}
          onDismiss={removeToast}
        />
      ))}
    </div>
  );
};

interface ToastItemProps {
  id: string;
  type: ToastType;
  message: string;
  onDismiss: (id: string) => void;
}

// Per-type visual variant. 'info' is the transient/neutral nudge (blue), distinct
// from the red error styling it would otherwise fall through to (LR-478 review r3).
const TOAST_VARIANT: Record<ToastType, { border: string; icon: typeof CheckCircleIcon; iconClass: string }> = {
  success: { border: 'border-l-green-500', icon: CheckCircleIcon, iconClass: 'text-green-500' },
  error: { border: 'border-l-red-500', icon: ExclamationCircleIcon, iconClass: 'text-red-500' },
  info: { border: 'border-l-blue-500', icon: InformationCircleIcon, iconClass: 'text-blue-500' },
};

const ToastItem: React.FC<ToastItemProps> = ({ id, type, message, onDismiss }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onDismiss(id);
    }, AUTO_DISMISS_MS);

    return () => clearTimeout(timer);
  }, [id, onDismiss]);

  const { border: borderClass, icon: IconComponent, iconClass } = TOAST_VARIANT[type];

  return (
    <div
      className={`flex items-center gap-3 rounded-md border border-gray-200 border-l-4 ${borderClass} bg-white px-4 py-3 shadow-lg animate-fade-in`}
      // Errors interrupt (assertive); success/info are polite status messages.
      role={type === 'error' ? 'alert' : 'status'}
    >
      <IconComponent className={`h-5 w-5 flex-shrink-0 ${iconClass}`} />
      <p className="text-sm text-gray-700 flex-1">{message}</p>
      <button
        onClick={() => onDismiss(id)}
        className="flex-shrink-0 text-gray-400 hover:text-gray-600"
        aria-label="Dismiss"
      >
        <XMarkIcon className="h-4 w-4" />
      </button>
    </div>
  );
};
